import React from 'react';
import AssessmentHeader from '../../exam-management/AssessmentHeader';
import AssessmentFooter from './AssessmentFooter';
import { ASSESSMENT } from '../../s3-assets/Assessment';

const Features = () => {
  return (
    <>
      <AssessmentHeader />
      <div className="relative overflow-hidden bg-white pt-12 pb-24 space-y-20 text-gray-900">

        {/* Feature 1: Idle Time and Inactivity Detection */}
        <div className="lg:mx-auto lg:grid lg:max-w-full lg:grid-cols-2 lg:gap-12 lg:px-20">
          {/* Image on the left */}
          <div className="flex items-center justify-center">
            <div className="lg:relative lg:h-80 lg:px-0">
              <img className="w-full h-full object-cover rounded-lg shadow-lg ring-1 ring-gray-300 hover:ring-2 hover:ring-blue-600 transition duration-300 ease-in-out" src={ASSESSMENT.IMAGES.IMAGE_2} alt="Idle Time Detection" />
            </div>
          </div>

          {/* Content on the right */}
          <div className="text-left flex flex-col justify-center">
            <h2 className="text-3xl font-extrabold text-[rgb(8,64,88)] mb-4 font-[sans-serif]">Idle Time and Inactivity Detection</h2>
            <p className="text-gray-600 text-sm leading-6">
              This feature ensures that the user is actively engaged during the entire assessment. If the user remains inactive for a specified period of time, an alert is triggered to notify the proctor or system administrator. By monitoring mouse movements, keyboard inputs, and window focus, we can detect idle time and prevent cheating or distractions. This is essential for ensuring the integrity of the exam process, as it discourages any attempts to leave the exam or perform other non-exam activities.
            </p>
            <ul className="list-disc text-sm text-gray-600 space-y-2 pl-4 mt-6">
              <li>Detects idle time and inactivity.</li>
              <li>Monitors mouse and keyboard activity.</li>
              <li>Prevents distractions during the exam.</li>
              <li>Ensures exam integrity by triggering alerts.</li>
            </ul>
            <div className="mt-6">
              <button type="button" className="bg-[rgb(8,64,88)] text-white text-sm font-semibold py-2 px-4 rounded-md hover:bg-blue-700 transition duration-300 ease-in-out">Get Started</button>
            </div>
          </div>
        </div>

        {/* Feature 2: Face Detection */}
        <div className="lg:mx-auto lg:grid lg:max-w-full lg:grid-cols-2 lg:gap-12 lg:px-20 lg:flex-row-reverse">
          {/* Content on the left */}
          <div className="text-left flex flex-col justify-center">
            <h2 className="text-3xl font-extrabold text-[rgb(8,64,88)] mb-4 font-[sans-serif]">Face Detection</h2>
            <p className="text-gray-600 text-sm leading-6">
              Face detection is a key security feature in our exam platform, leveraging real-time facial recognition technology to verify the identity of the exam taker. This helps prevent identity fraud and ensures that the correct person is taking the exam. Using face-api.js, we implement facial recognition that triggers alerts if the user’s face is not detected or if any abnormalities occur during the process. This enhances the reliability of online assessments, adding a layer of security and accountability to the entire testing experience.
            </p>
            <ul className="list-disc text-sm text-gray-600 space-y-2 pl-4 mt-6">
              <li>Uses facial recognition for identity verification.</li>
              <li>Detects abnormalities during the exam.</li>
              <li>Triggers alerts for unauthorized actions.</li>
            </ul>
            <div className="mt-6">
              <button type="button" className="bg-[rgb(8,64,88)] text-white text-sm font-semibold py-2 px-4 rounded-md hover:bg-blue-700 transition duration-300 ease-in-out">Get Started</button>
            </div>
          </div>

          {/* Image on the right */}
          <div className="flex items-center justify-center">
            <div className="lg:relative lg:h-80 lg:px-0">
              <img className="w-full h-full object-cover rounded-lg shadow-lg ring-1 ring-gray-300 hover:ring-2 hover:ring-blue-600 transition duration-300 ease-in-out" src={ASSESSMENT.IMAGES.IMAGE_1} alt="Face Detection" />
            </div>
          </div>
        </div>

        {/* Feature 3: Screen Capture Detection */}
        <div className="lg:mx-auto lg:grid lg:max-w-full lg:grid-cols-2 lg:gap-12 lg:px-20">
          {/* Image on the left */}
          <div className="flex items-center justify-center">
            <div className="lg:relative lg:h-80 lg:px-0">
              <img className="w-full h-full object-cover rounded-lg shadow-lg ring-1 ring-gray-300 hover:ring-2 hover:ring-blue-600 transition duration-300 ease-in-out" src={ASSESSMENT.IMAGES.IMAGE_3} alt="Screen Capture Detection" />
            </div>
          </div>

          {/* Content on the right */}
          <div className="text-left flex flex-col justify-center">
            <h2 className="text-3xl font-extrabold text-[rgb(8,64,88)] mb-4 font-[sans-serif]">Screen Capture Detection</h2>
            <p className="text-gray-600 text-sm leading-6">
              Our screen capture detection system helps safeguard the exam content from unauthorized copying. By continuously monitoring the screen during the assessment, the system can detect attempts to take screenshots or capture the screen. If such activity is detected, an alert is triggered. This feature prevents cheating by ensuring the exam material cannot be copied or shared, helping maintain the fairness and integrity of the assessment.
            </p>
            <ul className="list-disc text-sm text-gray-600 space-y-2 pl-4 mt-6">
              <li>Detects screen capture attempts.</li>
              <li>Ensures exam materials cannot be copied.</li>
              <li>Triggers alerts for unauthorized screen captures.</li>
            </ul>
            <div className="mt-6">
              <button type="button" className="bg-[rgb(8,64,88)] text-white text-sm font-semibold py-2 px-4 rounded-md hover:bg-blue-700 transition duration-300 ease-in-out">Get Started</button>
            </div>
          </div>
        </div>

        {/* Feature 4: Real-Time Activity Monitoring */}
        <div className="lg:mx-auto lg:grid lg:max-w-full lg:grid-cols-2 lg:gap-12 lg:px-20 lg:flex-row-reverse">
          {/* Content on the left */}
          <div className="text-left flex flex-col justify-center">
            <h2 className="text-3xl font-extrabold text-[rgb(8,64,88)] mb-4 font-[sans-serif]">Real-Time Activity Monitoring</h2>
            <p className="text-gray-600 text-sm leading-6">
              Real-time activity monitoring allows administrators and proctors to continuously track and log every action performed by the user during the exam. This includes mouse movements, clicks, and keyboard inputs. The system helps ensure that the user remains engaged in the exam and does not deviate from the testing environment. If any suspicious or unauthorized behavior is detected, alerts are immediately triggered. This adds a layer of security to the exam process and helps prevent cheating.
            </p>
            <ul className="list-disc text-sm text-gray-600 space-y-2 pl-4 mt-6">
              <li>Tracks mouse movements, clicks, and keyboard inputs.</li>
              <li>Ensures the user remains engaged during the exam.</li>
              <li>Triggers alerts for suspicious behavior.</li>
            </ul>
            <div className="mt-6">
              <button type="button" className="bg-[rgb(8,64,88)] text-white text-sm font-semibold py-2 px-4 rounded-md hover:bg-blue-700 transition duration-300 ease-in-out">Get Started</button>
            </div>
          </div>

          {/* Image on the right */}
          <div className="flex items-center justify-center">
            <div className="lg:relative lg:h-80 lg:px-0">
              <img className="w-full h-full object-cover rounded-lg shadow-lg ring-1 ring-gray-300 hover:ring-2 hover:ring-blue-600 transition duration-300 ease-in-out" src={ASSESSMENT.IMAGES.IMAGE_4} alt="Activity Monitoring" />
            </div>
          </div>
        </div>

        {/* Feature 5: Tab Switch and Window Focus Detection */}
        <div className="lg:mx-auto lg:grid lg:max-w-full lg:grid-cols-2 lg:gap-12 lg:px-20">
          {/* Image on the left */}
          <div className="flex items-center justify-center">
            <div className="lg:relative lg:h-80 lg:px-0">
              <img className="w-full h-full object-cover rounded-lg shadow-lg ring-1 ring-gray-300 hover:ring-2 hover:ring-blue-600 transition duration-300 ease-in-out" src={ASSESSMENT.IMAGES.IMAGE_5} alt="Tab Switch Detection" />
            </div>
          </div>

          {/* Content on the right */}
          <div className="text-left flex flex-col justify-center">
            <h2 className="text-3xl font-extrabold text-[rgb(8,64,88)] mb-4 font-[sans-serif]">Tab Switch and Window Focus Detection</h2>
            <p className="text-gray-600 text-sm leading-6">
              The system detects when the user switches browser tabs or minimizes the window, ensuring that they remain fully engaged in the exam. If such actions are detected, an alert is triggered. This feature prevents users from attempting to access external websites or applications during the exam, maintaining the security and integrity of the testing environment. It is a critical feature to ensure that the exam is taken with full focus and honesty.
            </p>
            <ul className="list-disc text-sm text-gray-600 space-y-2 pl-4 mt-6">
              <li>Detects tab switches or window minimizes.</li>
              <li>Prevents access to external sites during the exam.</li>
              <li>Maintains security and focus throughout the exam.</li>
            </ul>
            <div className="mt-6">
              <button type="button" className="bg-[rgb(8,64,88)] text-white text-sm font-semibold py-2 px-4 rounded-md hover:bg-blue-700 transition duration-300 ease-in-out">Get Started</button>
            </div>
          </div>
        </div>

      </div>
      <AssessmentFooter />
    </>
  );
};

export default Features;
